import React, { useEffect, useState } from 'react';
import {
  CheckboxGroup,
  Checkbox,
  Accordion,
  AccordionItem,
} from '@nextui-org/react';
import { useRouter } from 'next/router';
import { useProductLoader } from '../product/product-loader';

type OptionType = {
  name: string;
  id: string;
  children: OptionType[];
  parent: OptionType;
  label: string;
};

const truncateName = (name: string, maxLength: number = 23) => {
  return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name;
};

const hasAnyChildSelectedRecursive: any = (
  s: OptionType,
  selectedKeys: any[]
) => {
  const hasChildren = s.children && s.children.length > 0;
  if (hasChildren) {
    const hasAnyChildSelected = s.children.some((child) =>
      hasAnyChildSelectedRecursive(child, selectedKeys)
    );
    if (hasAnyChildSelected) return true;
  }
  return selectedKeys.includes(s.id);
};

const childrenIdsRecursive = (s: OptionType) => {
  let ids: string[] = [];
  if (s.children && s.children.length > 0) {
    s.children.forEach((child) => {
      ids.push(child.id.toString());
      ids = [...ids, ...childrenIdsRecursive(child)];
    });
  }
  return ids;
};

const CheckboxItem = ({
  option,
  selectedKeys,
}: {
  option: OptionType;
  selectedKeys: any[];
}) => {
  const hasChildren = option.children && option.children.length > 0;
  // const childrenIds = useMemo(() => childrenIdsRecursive(option), [option]);

  const [selectedAccordions, setSelectedAccordions] = useState(
    hasAnyChildSelectedRecursive(option, selectedKeys) ? [option.id] : []
  );

  // HACK: collapse accordion of parent with children selected
  useEffect(() => {
    if (
      hasAnyChildSelectedRecursive(option, selectedKeys) &&
      selectedAccordions.length < 1
    )
      setSelectedAccordions([option.id.toString()]);
  }, [selectedKeys, selectedAccordions]);

  return (
    <>
      {hasChildren ? (
        <Accordion
          isCompact
          disableAnimation
          style={{ padding: 0 }}
          selectionMode="multiple"
          selectedKeys={selectedAccordions}
          onSelectionChange={setSelectedAccordions}
          // defaultExpandedKeys={
          //   hasAnyChildSelectedRecursive(option, selectedKeys)
          //     ? [option.id]
          //     : []
          // }
        >
          <AccordionItem
            key={option.id.toString()}
            textValue={option.name} // Add this line
            startContent={
              <Checkbox
                key={option.id}
                value={option.id}
                name={option.id}
                isIndeterminate={
                  !selectedKeys.includes(option.id) &&
                  hasAnyChildSelectedRecursive(option, selectedKeys)
                }
                radius='sm'
                classNames={{
                  base: 'py-0',
                }}
              >
                {truncateName(option.name)}
              </Checkbox>
            }
          >
            <div className="ml-[28px]">
              {option.children.map((child: any) => (
                <CheckboxItem
                  key={child.id}
                  option={child}
                  selectedKeys={selectedKeys}
                />
              ))}
            </div>
          </AccordionItem>
        </Accordion>
      ) : (
        <div className="">
          <Checkbox
            key={option.id}
            value={option.id}
            name={option.id}
            radius='sm'
            isDisabled={option.name === 'Stable Diffusion'}
          >
            {option.name === 'Stable Diffusion' ? (
              <span className='italic'>more coming soon...</span>
            ) : (
              truncateName(option.name)
            )}
          </Checkbox>
          {hasChildren && (
            <div className="ml-[28px]">
              {option.children.map((child: any) => (
                <CheckboxItem
                  key={child.id}
                  option={child}
                  selectedKeys={selectedKeys}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
};

function ListboxFilter({
  // inputValue,
  selectedKeys,
  setSelectedKeys,
  items,
  queryParamName,
}: {
  // inputValue: any;
  selectedKeys: string[];
  setSelectedKeys: any;
  items: any;
  queryParamName: string;
}) {
  const router = useRouter();
  const { setIsLoading } = useProductLoader();

  const removeQueryParam = (param: any) => {
    // Create a new query object
    const newQuery = { ...router.query };

    // Delete the parameter to be removed
    delete newQuery[param];

    // Update the URL with the new query parameters
    // This will navigate without reloading the page
    router.push(
      {
        pathname: router.pathname,
        query: newQuery,
      },
      undefined,
      { shallow: true }
    );
  };

  // TODO: optimize this function?
  function handleChange(value: string[]) {
    setIsLoading(true);
    const newItem = items.find((i) => i.id === value[value.length - 1]);
    const hasChildren = newItem?.children && newItem?.children.length > 0;
    const hasParent = newItem?.parent;

    // Assuming `items` contains all options in a flat structure with parent-child relationships
    const allItems = {}; // Placeholder for mapping all items by id for quick access
    items.forEach((item) => {
      allItems[item.id] = item;
    });

    // Initialize with the newly selected or retained keys, assuming 'value' contains all selected ids
    let updatedSelectedKeys = [...value];

    if (hasParent) {
      // START: handle de-selecting parent when child is selected

      const findParents = (id, parents = []) => {
        const item = allItems[id];
        if (item && item.parent) {
          parents.push(item.parent.id);
          findParents(item.parent.id, parents);
        }
        return parents;
      };

      value.forEach((selectedId) => {
        const parents = findParents(selectedId);
        // Remove any parent IDs from the updated selection
        updatedSelectedKeys = updatedSelectedKeys.filter(
          (id) => !parents.includes(id)
        );
      });

      // un-select children if parent is selected
      updatedSelectedKeys = updatedSelectedKeys.filter(
        (id) =>
          !findParents(id).some((parent) =>
            updatedSelectedKeys.includes(parent)
          )
      );

      // END: handle de-selecting parent when child is selected
    }
    if (hasChildren) {
      // START: handle de-selecting all childrens when parent is selected
      const findChildren = (id, children = []) => {
        const item = allItems[id];
        if (item && item.children) {
          item.children.forEach((child) => {
            children.push(child.id);
            findChildren(child.id, children);
          });
        }
        return children;
      };

      value.forEach((selectedId) => {
        const children = findChildren(selectedId);
        // Remove any child IDs from the updated selection
        updatedSelectedKeys = updatedSelectedKeys.filter(
          (id) => !children.includes(id)
        );
      });
      // END: handle de-selecting all childrens when parent is selected
    }
    if (!updatedSelectedKeys.includes(newItem?.id)) {
      updatedSelectedKeys.push(newItem?.id);
    }

    setSelectedKeys(updatedSelectedKeys);

    // Manage url query params
    const slugs: string[] = [];
    updatedSelectedKeys.forEach((element) => {
      const it = items.find((i) => i.id === element || i.value === element);
      const slug = it?.value;
      if (slug) slugs.push(slug);
    });

    if (slugs.length < 1) {
      setSelectedKeys([]);
      return removeQueryParam(queryParamName);
    } else {
      setSelectedKeys(updatedSelectedKeys);
      router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            [queryParamName]: slugs.join(','),
          },
        },
        undefined,
        { scroll: false }
      );
    }
  }

  return (
    <div className="max-w-xs">
      {/* {topContent} */}
      <CheckboxGroup
        value={selectedKeys}
        color="primary"
        onValueChange={handleChange}
        disableAnimation
        classNames={
          {
            // base: `h-[230px] overflow-y-scroll custom-scrollbar px-2 pt-4 overflow-y-auto`,
          }
        }
      >
        {items
          // .filter((item) =>
          //   item.label.toLowerCase().includes(inputValue.toLowerCase())
          // )
          // ?
          .filter((value) => !value?.parent)
          .map((option: any) => (
            <CheckboxItem
              key={option?.id}
              option={option}
              selectedKeys={selectedKeys}
            />
          ))}
      </CheckboxGroup>
    </div>
  );
}

export default function AccordionFilter({
  items,
  queryParamName,
}: {
  items: any;
  queryParamName: string;
}) {
  const router = useRouter();
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  useEffect(() => {
    // Get the query value, handling both array and string cases
    const queryValue = router.query[queryParamName];
    const valueArray = Array.isArray(queryValue)
      ? queryValue
      : queryValue?.split(',') || [];

    // Find ids from items that match elements in valueArray
    const ids = valueArray
      .map(
        (element) =>
          items.find((item) => item.id === element || item.value === element)
            ?.id
      )
      .filter(Boolean); // Removes any undefined or null values

    // Update selectedKeys based on ids found, or clear if queryValue is falsy
    if (queryValue) {
      setSelectedKeys(ids);
    } else {
      setSelectedKeys([]);
    }
  }, [router.query, items]); // Depend on router.query and items to re-run effect

  return (
    <div className="w-full text-white">
      <ListboxFilter
        items={items}
        // inputValue={inputValue}
        selectedKeys={selectedKeys}
        setSelectedKeys={setSelectedKeys}
        queryParamName={queryParamName}
      />
    </div>
  );
}
